/**
 * Category-to-video mapping for article cards.
 * Each category has 2+ content-relevant videos to avoid adjacent duplicates.
 * Articles rotate through their category videos based on position index.
 * All videos are HD landscape format for card backgrounds.
 */

const CDN = 'https://customer-assets.emergentagent.com/job_b243a8fb-e07a-4745-aa9b-1afaf42d31b2/artifacts';
const PX = 'https://videos.pexels.com/video-files';

const CATEGORY_VIDEOS = {
  'HYROX': [
    // Battle ropes workout - core HYROX station exercise
    `${PX}/6388397/6388397-uhd_2560_1440_25fps.mp4`,
    // Crossfit functional fitness exercise
    `${PX}/6797133/6797133-hd_1920_1080_30fps.mp4`,
    // Marathon runner finish line - running station
    `${PX}/1632058/1632058-hd_1920_1080_25fps.mp4`,
  ],
  'Fitness': [
    // Woman lifting weights in gym
    `${PX}/3196428/3196428-hd_1920_1080_25fps.mp4`,
    // Man lifting kettlebell
    `${PX}/1502662/1502662-hd_1280_720_30fps.mp4`,
    // Battle ropes - intense training
    `${PX}/6388397/6388397-uhd_2560_1440_25fps.mp4`,
  ],
  'Martial Arts': [
    // Girl practicing martial arts in dojo
    `${PX}/7045130/7045130-hd_1920_1080_30fps.mp4`,
    // Men doing sparring in ring
    `${PX}/6296447/6296447-hd_1920_1080_25fps.mp4`,
    // Sparring wide shot
    `${PX}/6296579/6296579-uhd_2560_1080_25fps.mp4`,
  ],
  'Biohacking': [
    // Cinematic macro slow-motion science/supplements
    `${CDN}/2c2nu2f3_Cinematic_macro_slow-motion_fo_Kling_30__96147.mp4`,
    // Science lab / molecular visuals
    `${PX}/3195394/3195394-uhd_2560_1440_25fps.mp4`,
  ],
  'Peptides': [
    // Science lab / molecular visuals
    `${PX}/3195394/3195394-uhd_2560_1440_25fps.mp4`,
    // Cinematic macro slow-motion supplements
    `${CDN}/2c2nu2f3_Cinematic_macro_slow-motion_fo_Kling_30__96147.mp4`,
  ],
  'Meditation': [
    // Woman meditating in lotus position
    `${PX}/6740727/6740727-hd_1920_1080_25fps.mp4`,
    // Woman with healing bowl on beach
    `${PX}/8391445/8391445-hd_1920_1080_24fps.mp4`,
    // Zen candle / water drop
    `${CDN}/39959enl_Open_on_pure_black_A_single_wa_Kling_30__73720.mp4`,
  ],
  'Mindset': [
    // Woman meditating on beach
    `${PX}/8391363/8391363-hd_1920_1080_24fps.mp4`,
    // Zen candle / water drop
    `${CDN}/39959enl_Open_on_pure_black_A_single_wa_Kling_30__73720.mp4`,
  ],
  'Nutrition': [
    // Food preparation / healthy cooking
    `${PX}/1580507/1580507-hd_1920_1080_30fps.mp4`,
    // Marathon runners - athletic nutrition context
    `${PX}/3943396/3943396-hd_1920_1080_30fps.mp4`,
  ],
  'Wellness': [
    // Woman doing yoga
    `${PX}/4918024/4918024-hd_1920_1080_24fps.mp4`,
    // Women yoga exercise group
    `${PX}/8480746/8480746-hd_1920_1080_25fps.mp4`,
  ],
  'Motivation': [
    // People on a marathon race
    `${PX}/4612365/4612365-hd_1920_1080_24fps.mp4`,
    // Sport marathon runners
    `${PX}/3943396/3943396-hd_1920_1080_30fps.mp4`,
    // Crossfit training intensity
    `${PX}/6797133/6797133-hd_1920_1080_30fps.mp4`,
  ],
  'Hydrotherapy': [
    // Human diving into water
    `${CDN}/6emc8tin_Opens_with_the_human_dives_int_Kling_30__66176.mp4`,
    // Aerial slow-motion water pullback
    `${CDN}/1wviuaxx_Aerial_slow-motion_pull-back_f_Kling_30__05042.mp4`,
  ],
  'Culture': [
    // Men sparring / combat culture
    `${PX}/6296579/6296579-uhd_2560_1080_25fps.mp4`,
    // Girl practicing martial arts
    `${PX}/7045130/7045130-hd_1920_1080_30fps.mp4`,
  ],
  'Technology': [
    // Science lab / molecular
    `${PX}/3195394/3195394-uhd_2560_1440_25fps.mp4`,
    // Cinematic macro science
    `${CDN}/2c2nu2f3_Cinematic_macro_slow-motion_fo_Kling_30__96147.mp4`,
  ],
  'Lifestyle': [
    // Women yoga exercise
    `${PX}/8480746/8480746-hd_1920_1080_25fps.mp4`,
    // Woman meditating on beach
    `${PX}/8391363/8391363-hd_1920_1080_24fps.mp4`,
  ],
  'Recovery Science': [
    // Aerial water pullback
    `${CDN}/1wviuaxx_Aerial_slow-motion_pull-back_f_Kling_30__05042.mp4`,
    // Woman yoga / stretching
    `${PX}/4918024/4918024-hd_1920_1080_24fps.mp4`,
  ],
};

// Generic fallback pool for unmapped categories
const FALLBACK_VIDEOS = [
  `${PX}/3196428/3196428-hd_1920_1080_25fps.mp4`,
  `${PX}/6797133/6797133-hd_1920_1080_30fps.mp4`,
  `${PX}/4612365/4612365-hd_1920_1080_24fps.mp4`,
];

/**
 * Get a video URL for an article based on its category and index.
 * Ensures adjacent articles get different videos.
 * @param {string} category - Article category
 * @param {number} index - Article position in list (for rotation)
 * @returns {string|null} Video URL or null
 */
export function getVideoForArticle(category, index = 0) {
  const videos = CATEGORY_VIDEOS[category];
  if (videos && videos.length > 0) {
    return videos[index % videos.length];
  }
  return FALLBACK_VIDEOS[index % FALLBACK_VIDEOS.length];
}

export default CATEGORY_VIDEOS;
